import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/common/Modal';
import TextArea from '@/components/common/TextArea';
import Button from '@/components/common/Button';
import { useChatStore } from '@/stores/chatStore';

interface TaskMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskTitle: string;
}

/**
 * Modal for composing a message to Jerry about a specific Move (task).
 *
 * On submit, the message is prepended with "Re: Task [title]" for context,
 * stored as a pending message, and the user is navigated to the Messages view
 * where it is automatically sent.
 */
export default function TaskMessageModal({ isOpen, onClose, taskTitle }: TaskMessageModalProps) {
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const setPendingNewMessage = useChatStore((s) => s.setPendingNewMessage);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = content.trim();
    if (!trimmed) {
      setError('Message is required');
      return;
    }

    // Construct the full message with task context
    const fullMessage = `Re: Task "${taskTitle}"\n\n${trimmed}`;

    // Store the pending message and navigate to new conversation
    setPendingNewMessage(fullMessage);
    handleClose();
    navigate('/app/messages/new');
  };

  const handleClose = () => {
    setContent('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Message Jerry" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-neutral-600">
          Ask a question or share feedback about <span className="font-medium text-neutral-800">{taskTitle}</span>
        </p>

        <div>
          <TextArea
            id="task-message-content"
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Type your message..."
            rows={4}
            maxLength={2000}
            error={error || undefined}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!content.trim()}
          >
            Send Message
          </Button>
        </div>
      </form>
    </Modal>
  );
}
